import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { CategoryIcon } from '@/features/catalog/CategoryIcon';
import { categoryHref } from '@/features/catalog/categorySlug';
import { categoryStyle, categoryVars } from '@/features/catalog/categoryStyle';
import { useCatalogCategoriesQuery } from '@/features/catalog/queries';
// Submodule path: the barrel would pull the whole inactivity feature into the shell.
import { NotificationBell } from '@/features/inactivity/NotificationBell';
import { formatClosingTime } from '@/features/orders/closingWindow';

import { CartBadge } from '../CartBadge';
import { useCommandPaletteShortcut } from '../useCommandPaletteShortcut';
import { StoreAccountMenu, StoreDrawer } from './StoreAccountMenu';
import { StoreSearch } from './StoreSearch';
import type { StoreSummary } from './useStoreSummary';
import styles from './StoreHeader.module.css';

export interface StoreHeaderProps {
  readonly summary: StoreSummary;
  /** "Ajuda" — the ⌘K palette with every page the profile reaches (and the theme). */
  readonly onHelp: () => void;
}

function CategoriesMenu() {
  const categories = useCatalogCategoriesQuery();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.categories} ref={rootRef}>
      <button
        type="button"
        className={styles.categoriesButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Icon name="menu" size={18} strokeWidth={1.6} />
        Categorias
        <Icon name="chevronDown" size={14} strokeWidth={2} />
      </button>
      {open ? (
        <div className={styles.categoriesMenu} role="menu" aria-label="Categorias">
          <Link
            to="/catalogo"
            role="menuitem"
            className={styles.categoryItem}
            onClick={() => {
              setOpen(false);
            }}
          >
            <span className={styles.categoryDot} data-all="">
              <Icon name="grid" size={16} />
            </span>
            Todas as categorias
          </Link>
          {(categories.data ?? []).map((name) => {
            const style = categoryStyle(name);
            return (
              <Link
                key={name}
                to={categoryHref(name)}
                role="menuitem"
                className={styles.categoryItem}
                viewTransition
                onClick={() => {
                  setOpen(false);
                }}
              >
                <span className={styles.categoryDot} style={categoryVars(style)}>
                  <CategoryIcon style={style} size={16} />
                </span>
                {name}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The storefront header for the profiles that buy (Figma D1, M1). The search is the centre of it;
 * the unit the orders leave from, the bell, the cart with its total and the account sit on the
 * right; a second line carries buying shortcuts, the open order and "Ajuda". It replaces the
 * "Comprar | Pedidos" tabs of the top bar — the order list now lives in the account menu, the
 * open-order pill and the home's "Pedido aberto" card.
 *
 * On a phone the line folds to menu · brand · bell · cart, the search takes a row of its own and
 * a thin row says where the order goes and when it closes; everything else is in the drawer.
 *
 * The establishment is shown, not offered: switching units is not possible yet
 * (`useCurrentEstablishment`), so the Figma's "Trocar" is left out until it is.
 */
export function StoreHeader({ summary, onHelp }: StoreHeaderProps) {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ⌘K focuses the search on every screen (Figma D2: "⌘K foca a busca em qualquer tela").
  const focusSearch = useCallback(() => {
    searchRef.current?.focus();
  }, []);
  useCommandPaletteShortcut(focusSearch);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const open = summary.openOrder;

  return (
    <header className={styles.header} data-topbar="" data-store-header="">
      <div className={styles.main}>
        <button
          type="button"
          className={styles.menuButton}
          aria-label="Abrir o menu"
          aria-expanded={drawerOpen}
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          <Icon name="menu" size={24} />
        </button>
        <Link to="/" className={styles.brand} viewTransition>
          <span className={styles.mark} aria-hidden="true">
            In
          </span>
          Instoque
        </Link>
        <div className={styles.searchBar}>
          <StoreSearch ref={searchRef} summary={summary} />
        </div>
        {summary.establishmentName !== undefined ? (
          <p className={styles.unit} title={summary.establishmentName}>
            <Icon name="pin" size={22} strokeWidth={1.65} />
            <span className={styles.unitText}>
              <span className={styles.unitLabel}>Entregar em</span>
              <span className={styles.unitName}>{summary.establishmentName}</span>
            </span>
          </p>
        ) : null}
        <div className={styles.tools}>
          <NotificationBell tone="on-brand" />
          <CartBadge tone="on-brand" />
          <div className={styles.account}>
            <StoreAccountMenu summary={summary} onHelp={onHelp} />
          </div>
        </div>
      </div>

      <div className={styles.searchRow}>
        <StoreSearch summary={summary} variant="row" />
      </div>

      <div className={styles.phoneScope}>
        {summary.establishmentName !== undefined ? (
          <span className={styles.phoneUnit}>
            <Icon name="pin" size={14} />
            Entregar em {summary.establishmentName}
          </span>
        ) : (
          <span />
        )}
        {open !== undefined ? (
          <Link to={`/pedidos/${open.id}`} className={styles.phoneClose}>
            Fecha às {formatClosingTime(open.closes_at)}
          </Link>
        ) : null}
      </div>

      <nav className={styles.shortcuts} aria-label="Atalhos de compra">
        <CategoriesMenu />
        <Link to="/#comprar-de-novo" className={styles.shortcut}>
          Comprar de novo
        </Link>
        <Link to="/#mais-comprados" className={styles.shortcut}>
          Mais comprados
        </Link>
        <NavLink to="/catalogo" end className={styles.shortcut ?? ''} viewTransition>
          Lista rápida
        </NavLink>
        <span className={styles.spacer} />
        {open !== undefined ? (
          <Link to={`/pedidos/${open.id}`} className={styles.openPill} viewTransition>
            <Icon name="clock" size={16} strokeWidth={1.4} />
            Pedido aberto · fecha às {formatClosingTime(open.closes_at)}
          </Link>
        ) : null}
        <button type="button" className={styles.help} onClick={onHelp}>
          <Icon name="help" size={16} strokeWidth={1.4} />
          Ajuda
        </button>
      </nav>

      <StoreDrawer open={drawerOpen} onClose={closeDrawer} summary={summary} onHelp={onHelp} />
    </header>
  );
}
